import React, { useContext, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { ReactComponent as PencilIcon } from 'assets/icons/ui-elements/small/pencil-sm.svg';
import { CalendarEvent } from 'pages/Agenda/utils/eventUtils';
import { AgendaTarefa, AgendaService } from 'services/reports/endpoints/AgendaService';
import { EventColorSet } from 'pages/Agenda/utils/eventColors';
import { AuthContext } from 'context/AuthContext';
import { parseJwt } from 'utils/parse';
import { useCookies } from 'react-cookie';
import { useHeaderFilters } from 'context/GlobalFiltersContext';
import { useQueryClient } from 'react-query';
import { useToast } from 'hooks/useToast';
import { createErrorToast, createGenericErrorToast } from 'utils/createToast';
import { ModalHeader } from './ModalHeader';
import { ActivityList } from './ActivityList';
import { EditActivityList, RowEdit } from './EditActivityList';
import { ConfirmDeleteModal } from './ConfirmDeleteModal';
import { ConfirmRestoreModal } from './ConfirmRestoreModal';
import { ApplyChangesModal } from './ApplyChangesModal';
import { useAgendaTarefas } from './useAgendaTarefas';
import { Overlay, ModalBody, HeaderActions, EditButton, LoadingWrapper } from './styled';

interface PlanApplicationModalProps {
  event: CalendarEvent;
  colorSet: EventColorSet;
  isCompletedView: boolean;
  canEdit: boolean;
  canDelete: boolean;
  onClose: () => void;
  onEditProgramacao: (event: CalendarEvent) => void;
  onRestaurar: (event: CalendarEvent) => Promise<void>;
  onExcluir: (tarefa: AgendaTarefa) => Promise<void>;
  onSaveSuccess?: () => void;
}

export const PlanApplicationModal: React.FC<PlanApplicationModalProps> = ({
  event,
  colorSet,
  isCompletedView,
  canEdit,
  canDelete,
  onClose,
  onRestaurar,
  onExcluir,
  onSaveSuccess,
}) => {
  const { authContext } = useContext(AuthContext);
  const [cookie] = useCookies();
  const { headerFiltersContext } = useHeaderFilters();
  const { headerFilters } = headerFiltersContext;
  const queryClient = useQueryClient();
  const { setToastList } = useToast();

  const showError = (message?: string) => {
    if (!setToastList) return;
    if (message) createErrorToast(setToastList, message);
    else createGenericErrorToast(setToastList);
  };

  const [selectedTarefa, setSelectedTarefa] = useState<AgendaTarefa | null>(null);
  const [selectedRestoreTarefa, setSelectedRestoreTarefa] = useState<AgendaTarefa | null>(null);
  const [bulkDeleteTarefas, setBulkDeleteTarefas] = useState<AgendaTarefa[] | null>(null);
  const [isActioning, setIsActioning] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [pendingEdits, setPendingEdits] = useState<Record<number, RowEdit> | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const status = isCompletedView ? 1 : 0;

  const { data, isLoading, isError } = useAgendaTarefas({
    aplicacaoPlanoAtividadeId: event.aplicacaoPlanoAtividadeId,
    primeiraDataHora: event.primeiraDataHora,
    status,
  });

  const getUsuarioId = (): number => {
    if (!authContext?.token) return 0;
    const { nameid } = parseJwt(authContext.token);
    return Number(nameid);
  };

  const getEmpresaId = (): number => Number(cookie.companies?.id ?? 0);

  const getUnidadeId = (): number | undefined => {
    const id = headerFilters?.units?.id;
    return id ? Number(id) : undefined;
  };

  const sentryAttrs = () => ({
    empresa_id: getEmpresaId(),
    aplicacao_id: event.aplicacaoPlanoAtividadeId,
  });

  useEffect(() => {
    Sentry.captureMessage('Abriu modal do card da agenda', {
      level: 'info',
      extra: { empresa_id: getEmpresaId(), aplicacao_id: event.aplicacaoPlanoAtividadeId },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClickEditar = () => {
    Sentry.captureMessage('Clicou em editar programação', { level: 'info', extra: sentryAttrs() });
    setIsEditing(true);
  };

  const handleSaveEdits = (edits: Record<number, RowEdit>) => {
    Sentry.captureMessage('Clicou em salvar agendamento', { level: 'info', extra: sentryAttrs() });
    setPendingEdits(edits);
  };

  const handleApplyConfirm = async (applyFuture: boolean) => {
    if (isActioning || !data || !pendingEdits) return;
    setIsActioning(true);
    try {
      const itens = data.tarefas
        .filter(t => pendingEdits[t.numero])
        .map(t => {
          const edit = pendingEdits[t.numero];
          const originalDate = t.dataPrevista ? t.dataPrevista.split('T')[0] : '';
          const originalTime = t.dataPrevista ? (t.dataPrevista.split('T')[1] ?? '').slice(0, 5) : '';
          const date = edit.date || originalDate;
          const time = edit.time || originalTime;
          const dataPrevista = date && time ? `${date}T${time}:00` : t.dataPrevista;
          const originalExecutores = t.executores.map(e => e.executorId).filter(Boolean);
          const executores =
            edit.executorIds.length > 0 ? edit.executorIds.map(Number).filter(Boolean) : originalExecutores;
          return {
            tarefa: t.numero,
            dataPrevista,
            tempo: t.tempoPrevisto ?? 0,
            executores,
          };
        });

      if (applyFuture) {
        Sentry.captureMessage('Salvou agendamento futuro', { level: 'info', extra: sentryAttrs() });
        Sentry.logger.info('Alterando agendamento de aplicação (atividades futuras)', sentryAttrs());
        const res = await AgendaService.alterarAgendamentoAplicacao({
          usuarioId: getUsuarioId(),
          empresaId: getEmpresaId(),
          unidadeId: getUnidadeId(),
          itens: itens.map(i => ({ tarefa: i.tarefa, dataReferencia: i.dataPrevista, executores: i.executores })),
        });
        if (res?.data?.success === false) {
          showError(res.data.message);
          return;
        }
      } else {
        Sentry.captureMessage('Salvou agendamento atual', { level: 'info', extra: sentryAttrs() });
        Sentry.logger.info('Alterando agendamento de tarefas (somente selecionadas)', sentryAttrs());
        const res = await AgendaService.alterarAgendamento({
          usuarioId: getUsuarioId(),
          empresaId: getEmpresaId(),
          unidadeId: getUnidadeId(),
          itens,
        });
        if (res?.data?.success === false) {
          showError(res.data.message);
          return;
        }
      }

      setSaveSuccess(true);
    } catch (error: any) {
      Sentry.captureException(error, { tags: { action: 'alterar_agendamento', ...sentryAttrs() } });
      showError(error?.response?.data?.message);
    } finally {
      setIsActioning(false);
    }
  };

  const handleSuccessClose = () => {
    setSaveSuccess(false);
    setPendingEdits(null);
    setIsEditing(false);
    if (onSaveSuccess) onSaveSuccess();
    else onClose();
  };

  const handleRequestExcluir = (tarefa: AgendaTarefa) => {
    Sentry.captureMessage('Clicou em excluir tarefa', {
      level: 'info',
      extra: { ...sentryAttrs(), tarefa_id: tarefa.numero },
    });
    setSelectedTarefa(tarefa);
  };

  const handleRequestBulkExcluir = (tarefas: AgendaTarefa[]) => {
    setBulkDeleteTarefas(tarefas);
  };

  const handleConfirmExcluir = async () => {
    if (!selectedTarefa || isActioning) return;
    setIsActioning(true);
    try {
      Sentry.logger.info('Excluindo tarefa da agenda', { ...sentryAttrs(), tarefa_id: selectedTarefa.numero });
      const res = await AgendaService.deleteAgendaTarefas({
        tarefasIds: [selectedTarefa.numero],
        usuarioId: getUsuarioId(),
        empresaId: getEmpresaId(),
        unidadeId: getUnidadeId(),
      });
      if (res?.data?.success === false) {
        showError(res.data.message);
        return;
      }
      queryClient.invalidateQueries(['agendaTarefas']);
      setSelectedTarefa(null);
      await onExcluir(selectedTarefa);
      onClose();
    } catch (error: any) {
      Sentry.captureException(error, { tags: { action: 'excluir_tarefa', ...sentryAttrs() } });
      showError(error?.response?.data?.message);
    } finally {
      setIsActioning(false);
    }
  };

  const handleConfirmBulkExcluir = async () => {
    if (!bulkDeleteTarefas?.length || isActioning) return;
    setIsActioning(true);
    try {
      Sentry.logger.info('Excluindo tarefas em lote da agenda', {
        ...sentryAttrs(),
        tarefas_ids: bulkDeleteTarefas.map(t => t.numero),
      });
      const res = await AgendaService.deleteAgendaTarefas({
        tarefasIds: bulkDeleteTarefas.map(t => t.numero),
        usuarioId: getUsuarioId(),
        empresaId: getEmpresaId(),
        unidadeId: getUnidadeId(),
      });
      if (res?.data?.success === false) {
        showError(res.data.message);
        return;
      }
      queryClient.invalidateQueries(['agendaTarefas']);
      setBulkDeleteTarefas(null);
      onClose();
    } catch (error: any) {
      Sentry.captureException(error, { tags: { action: 'excluir_tarefas_lote', ...sentryAttrs() } });
      showError(error?.response?.data?.message);
    } finally {
      setIsActioning(false);
    }
  };

  const handleConfirmRestore = async (excluirFotosAssinaturas: boolean) => {
    if (!selectedRestoreTarefa || isActioning) return;
    setIsActioning(true);
    try {
      Sentry.captureMessage('Clicou em restaurar tarefa', {
        level: 'info',
        extra: { ...sentryAttrs(), tarefa_id: selectedRestoreTarefa.numero },
      });
      Sentry.logger.info('Restaurando tarefa da agenda', {
        ...sentryAttrs(),
        tarefa_id: selectedRestoreTarefa.numero,
      });
      const res = await AgendaService.restaurarTarefas({
        tarefasIds: [selectedRestoreTarefa.numero],
        excluirFotosAssinaturas,
        usuarioId: getUsuarioId(),
        empresaId: getEmpresaId(),
        unidadeId: getUnidadeId(),
      });
      if (res?.data?.success === false) {
        showError(res.data.message);
        return;
      }
      queryClient.invalidateQueries(['agendaTarefas']);
      setSelectedRestoreTarefa(null);
      await onRestaurar(event);
      onClose();
    } catch (error: any) {
      Sentry.captureException(error, { tags: { action: 'restaurar_tarefa', ...sentryAttrs() } });
      showError(error?.response?.data?.message);
    } finally {
      setIsActioning(false);
    }
  };

  return (
    <>
      <Overlay onClick={onClose} role="dialog" aria-modal="true">
        <ModalBody onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <ModalHeader event={event} colorSet={colorSet} isCompletedView={isCompletedView} onClose={onClose} />

          {canEdit && !isEditing && !isCompletedView && (
            <HeaderActions>
              <EditButton onClick={handleClickEditar}>
                <PencilIcon />
                Editar programação
              </EditButton>
            </HeaderActions>
          )}

          {isLoading && <LoadingWrapper>Carregando atividades...</LoadingWrapper>}
          {isError && <LoadingWrapper>Erro ao carregar atividades.</LoadingWrapper>}
          {data && !isEditing && (
            <ActivityList
              tarefas={data.tarefas}
              isCompletedView={isCompletedView}
              canDelete={canDelete}
              canRestore={canEdit}
              onRequestExcluir={handleRequestExcluir}
              onRequestRestaurar={setSelectedRestoreTarefa}
            />
          )}
          {data && isEditing && (
            <EditActivityList
              tarefas={data.tarefas}
              isSaving={isActioning}
              canDelete={canDelete}
              onSave={handleSaveEdits}
              onRequestExcluir={handleRequestExcluir}
              onBulkExcluir={handleRequestBulkExcluir}
            />
          )}
        </ModalBody>
      </Overlay>

      {selectedTarefa && (
        <ConfirmDeleteModal
          isActioning={isActioning}
          onConfirm={handleConfirmExcluir}
          onCancel={() => setSelectedTarefa(null)}
        />
      )}

      {bulkDeleteTarefas && (
        <ConfirmDeleteModal
          isActioning={isActioning}
          onConfirm={handleConfirmBulkExcluir}
          onCancel={() => setBulkDeleteTarefas(null)}
        />
      )}

      {selectedRestoreTarefa && (
        <ConfirmRestoreModal
          isActioning={isActioning}
          onConfirm={handleConfirmRestore}
          onCancel={() => setSelectedRestoreTarefa(null)}
        />
      )}

      {(pendingEdits || saveSuccess) && (
        <ApplyChangesModal
          isSaving={isActioning}
          isSuccess={saveSuccess}
          onClose={() => setPendingEdits(null)}
          onSuccessClose={handleSuccessClose}
          onConfirm={handleApplyConfirm}
        />
      )}
    </>
  );
};
